import { EMAIL_REGEX } from "../constants/Login.constants";

export const addOrdinalSuffix = (arr) => {
  return arr.map((item) => {
    return addIndividualOrdinalSuffix(item);
  });
};

export const addIndividualOrdinalSuffix = (nr) => {
  const remainder = nr % 10;
  const twoDigitNumber = nr % 100;
  if (remainder === 1 && twoDigitNumber !== 11) {
    return `${nr}st`;
  }
  if (remainder === 2 && twoDigitNumber !== 12) {
    return `${nr}nd`;
  }
  if (remainder === 3 && twoDigitNumber !== 13) {
    return `${nr}rd`;
  }
  return `${nr}th`;
};

export const createDaysDropdownValues = (minDay, maxDay, key) => {
  const size = maxDay - minDay + 1;
  if (!Number.isFinite(size) || size < 0) return [];
  const dayValues = [...Array(size).keys()].map(
    (i) => i + minDay,
  );
  const dayLabels = addOrdinalSuffix(dayValues);
  return dayValues.map((day, index) => {
    return {
      key: `${key}-${day}`,
      value: day,
      label: dayLabels[index],
    };
  });
};

export const checkValidity = (target, setErrorMessage) => {
  if (target.validity.valid) {
    setErrorMessage("");
    return;
  }
  
  // Format validation messages to show proper decimal places
  let message = target.validationMessage;
  
  // Check if the message contains a number that should be formatted
  if (message.includes('Value must be less than or equal to')) {
    const match = message.match(/Value must be less than or equal to ([\d.]+)/);
    if (match) {
      const value = parseFloat(match[1]);
      if (!isNaN(value)) {
        const formattedValue = value.toFixed(2);
        message = message.replace(match[1], formattedValue);
      }
    }
  }
  
  // Also handle "Value must be greater than or equal to" messages
  if (message.includes('Value must be greater than or equal to')) {
    const match = message.match(/Value must be greater than or equal to ([\d.]+)/);
    if (match) {
      const value = parseFloat(match[1]);
      if (!isNaN(value)) {
        const formattedValue = value.toFixed(2);
        message = message.replace(match[1], formattedValue);
      }
    }
  }
  
  // Handle any other validation messages that might contain numbers
  // This will catch cases like "Value must be less than or equal to 25" and format them
  const numberPattern = /(\d+(?:\.\d+)?)/g;
  message = message.replace(numberPattern, (match) => {
    const value = parseFloat(match);
    if (!isNaN(value)) {
      return value.toFixed(2);
    }
    return match;
  });
  
  setErrorMessage(message);
};

export const handleInputChange = (e, setter, setErrorMessage = null) => {
  e.preventDefault();
  setter(e.target.value);
  if (setErrorMessage !== null) {
    checkValidity(e.target, setErrorMessage);
  }
};

export const prefillInput = (target, value, setter, setErrorMessage) => {
  setter(value);
  // TODO: Find better async callback
  setTimeout(() => {
    checkValidity(target, setErrorMessage);
  }, 0);
};

export const hex2rgba = (hex, alpha = 1) => {
  const [r, g, b] = hex
    .match(hex.length <= 4 ? /\w/g : /\w\w/g)
    .map((x) => parseInt(x.length < 2 ? `${x}${x}` : x, 16));
  return `rgba(${r},${g},${b},${alpha})`;
};

export const padCents = (str) => {
  if (!str.includes(".")) {
    return str.concat(".00");
  }
  if (str.split(".")[1].length === 1) {
    return str.concat("0");
  }
  return str;
};

export const toDateStringNoDay = (dateLocaleString) => {
  const dateString = new Date(dateLocaleString).toDateString().split(" ");
  dateString.shift();
  return dateString.join(", ").replace(",", "");
};

export const createTableData = (row, rowAttributes) => {
  return Object.fromEntries(
    rowAttributes.map((_, i) => [rowAttributes[i], row[i]]),
  );
};

export function capitalizeFirstLetterOnly(word) {
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

export function capitalizeFirstLetters(word) {
  return word
    .split(" ")
    .map((item) => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase())
    .join(" ");
}

export function findElementIncludedInArray(
  arr,
  filterKey,
  filterValue,
  findKey,
  findValue,
) {
  return arr
    .filter((item) => item[filterKey] === filterValue)
    .some((item) => item[findKey] === findValue);
}

export const convertTwentyFourToTwelveHourFormat = (time) => {
  time = time.toString().match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/) || [
    time,
  ];

  if (time.length > 1) {
    time = time.slice(1);
    time[5] = +time[0] < 12 ? "AM" : "PM";
    time[0] = +time[0] % 12 || 12;
  }
  return time.join("");
};

export const removeNonDigits = (value) => {
  if (value == null || value === "") {
    return "";
  }

  return String(value).replace(/[^\d]/g, "");
};

export const formatPhoneNumber = (value) => {
  if (!value) return value;

  const phoneNumber = removeNonDigits(value);

  const phoneNumberLength = phoneNumber.length;

  // we need to return the value with no formatting if its less then four digits
  // this is to avoid weird behavior that occurs if you format the area code too early
  if (phoneNumberLength < 4) return phoneNumber;

  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }

  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
    3,
    6,
  )}-${phoneNumber.slice(6, 10)}`;
};

export const formatSocialSecurityNumber = (value) => {
  if (!value) return value;

  const socialSecurityNumber = removeNonDigits(value);

  const socialSecurityNumberLength = socialSecurityNumber.length;

  // we need to return the value with no formatting if its less then four digits
  // this is to avoid weird behavior that occurs if you format the area code too early
  if (socialSecurityNumberLength < 4) {
    return socialSecurityNumber;
  }

  if (socialSecurityNumberLength < 6) {
    return `${socialSecurityNumber.slice(0, 3)}-${socialSecurityNumber.slice(
      3,
    )}`;
  }

  return `${socialSecurityNumber.slice(0, 3)}-${socialSecurityNumber.slice(
    3,
    5,
  )}-${socialSecurityNumber.slice(5, 9)}`;
};

export const findInArray = (arr, key, value) => {
  return arr.find((item) => item[key] === value);
};

export const isAllowed = (element, key, value, defaultReturn) => {
  return element ? element[key] === value : defaultReturn;
};

/** Properties / radio settings may store true as boolean, "true", "1", or "yes". */
export const parseConfigBoolean = (value) => {
  if (value === true || value === 1) {
    return true;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "1" || normalized === "yes";
  }
  return false;
};

/** Pay Near Me smart links must be a valid https URL. */
export const isValidPnmSmartLink = (url) => {
  if (!url || typeof url !== "string") {
    return false;
  }

  const trimmed = url.trim();
  if (!/^https:\/\//i.test(trimmed)) {
    return false;
  }

  try {
    return new URL(trimmed).protocol === "https:";
  } catch {
    return false;
  }
};

// The tenant-service serializes httpStatus as an integer (200, 404, etc.).
// Also accept legacy string forms ("OK", "NO_CONTENT") for backward compatibility.
export const isBatchResponseOk = (httpStatus) =>
  httpStatus === 200 || httpStatus === 204 ||
  httpStatus === "OK" || httpStatus === "NO_CONTENT";

export const extractShownLoanNumber = (source) => {
  if (source == null || typeof source !== "object") {
    return "";
  }
  const shownLoanNumber =
    source.loanDetails?.shownLoanNumber ?? source.shownLoanNumber;
  if (shownLoanNumber == null) {
    return "";
  }
  return String(shownLoanNumber).trim();
};

/** Prefer loanDetails.shownLoanNumber when set; otherwise format the internal loan id. */
export const getDisplayLoanNumber = (loanId, context = null) => {
  const shownLoanNumber = extractShownLoanNumber(context);
  if (shownLoanNumber) {
    return shownLoanNumber;
  }
  return formatLoanNumberForDisplay(loanId);
};

/** Navbar loan label: omit leading letters when loan id starts with A–Z (e.g. UH123 → 123). */
export const formatLoanNumberForDisplay = (loanId) => {
  if (loanId == null || loanId === "" || loanId === "no-loan") {
    return loanId ?? "";
  }
  const s = String(loanId);
  if (!/^[A-Za-z]/.test(s)) {
    return s;
  }
  const withoutLeadingLetters = s.replace(/^[A-Za-z]+/, "");
  return withoutLeadingLetters || s;
};

export const hasValidLoanNickname = (nickname) =>
  typeof nickname === "string" && nickname.trim().length > 0;

export const getLoanId = (loan) => {
  if (loan == null || loan === "no-loan") {
    return loan ?? "";
  }
  if (typeof loan === "string" || typeof loan === "number") {
    return String(loan);
  }
  if (typeof loan === "object") {
    return String(
      loan.loanId ??
        loan.loanID ??
        loan.id ??
        loan.loan ??
        loan.loanNumber ??
        "",
    );
  }
  return "";
};

export const getLoanNickname = (loan) => {
  if (loan == null || typeof loan !== "object") {
    return "";
  }
  return String(
    loan.loanNickname ??
      loan.loanNickName ??
      loan.nickname ??
      loan.loan_nickname ??
      loan.loanDetails?.loanNickname ??
      loan.loanDetails?.loanNickName ??
      "",
  ).trim();
};

export const extractLoanNicknameFromDetails = (details) => {
  if (!details || typeof details !== "object") {
    return "";
  }
  return getLoanNickname(details);
};

export const parseLoansApiPayload = (data) => {
  if (Array.isArray(data)) {
    return data;
  }
  if (data && typeof data === "object") {
    if (Array.isArray(data.loans)) {
      return data.loans;
    }
    if (Array.isArray(data.loanList)) {
      return data.loanList;
    }
    if (Array.isArray(data.registeredLoans)) {
      return data.registeredLoans;
    }
  }
  return [];
};

export const normalizeLoanEntry = (entry) => {
  if (entry == null) {
    return null;
  }
  if (typeof entry === "string" || typeof entry === "number") {
    return { loanId: String(entry), loanNickname: "" };
  }
  if (typeof entry === "object") {
    const loanId = getLoanId(entry);
    if (!loanId) {
      return null;
    }
    const shownLoanNumber = extractShownLoanNumber(entry);
    return {
      loanId,
      loanNickname: getLoanNickname(entry),
      ...(shownLoanNumber ? { shownLoanNumber } : {}),
    };
  }
  return null;
};

export const normalizeLoansList = (list) => {
  if (!Array.isArray(list)) {
    return [];
  }
  return list.map(normalizeLoanEntry).filter(Boolean);
};

export const loansMatch = (loanEntry, activeLoanId) =>
  getLoanId(loanEntry) === activeLoanId;

export const formatLoanDropdownLabel = (loan, activeLoanDetails = null) => {
  const loanId = getLoanId(loan);
  const detailsLoanId = getLoanId(activeLoanDetails?.loanDetails);
  const shownLoanNumber =
    extractShownLoanNumber(loan) ||
    (activeLoanDetails && loansMatch(loan, detailsLoanId)
      ? extractShownLoanNumber(activeLoanDetails)
      : "");
  const id = shownLoanNumber || formatLoanNumberForDisplay(loanId);
  const nickname =
    getLoanNickname(loan) ||
    (activeLoanDetails && loansMatch(loan, detailsLoanId)
      ? extractLoanNicknameFromDetails(activeLoanDetails)
      : "");
  if (hasValidLoanNickname(nickname)) {
    return `${id} — ${nickname}`;
  }
  return id;
};

export const formatActiveLoanNavbarLabel = (
  activeLoanId,
  loans,
  activeLoanDetails = null,
) => {
  if (!activeLoanId || activeLoanId === "no-loan") {
    return "";
  }
  const entry = loans?.find((l) => loansMatch(l, activeLoanId));
  if (entry && hasValidLoanNickname(getLoanNickname(entry))) {
    return formatLoanDropdownLabel(entry);
  }
  const detailNickname = extractLoanNicknameFromDetails(activeLoanDetails);
  if (hasValidLoanNickname(detailNickname)) {
    const displayId = getDisplayLoanNumber(
      activeLoanId,
      entry?.shownLoanNumber ? entry : activeLoanDetails,
    );
    return `${displayId} — ${detailNickname}`;
  }
  return formatLoanDropdownLabel(entry || activeLoanId, activeLoanDetails);
};

export const mergeLoanNicknameInList = (loans, loanId, nickname) => {
  if (!Array.isArray(loans) || !loanId || !hasValidLoanNickname(nickname)) {
    return loans;
  }
  return loans.map((entry) => {
    if (!loansMatch(entry, loanId)) {
      return entry;
    }
    const base = normalizeLoanEntry(entry) || { loanId, loanNickname: "" };
    return { ...base, loanNickname: nickname };
  });
};

export const validateEmail = (email) => {
  return String(email).toLowerCase().match(EMAIL_REGEX);
};

export const blobToBase64 = (blob) => {
  return new Promise((resolve, _) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
};

export const getFileExtension = (fileName) => {
  const dotIndex = String(fileName ?? "").lastIndexOf(".");
  return dotIndex > -1 ? String(fileName).slice(dotIndex + 1).toLowerCase() : "";
};

/**
 * Predicts whether tenant-service will accept the file, which pairs the
 * extension with the browser-declared content type and rejects a mismatch.
 * An unrecognised extension leaves type empty, and the browser then sends
 * application/octet-stream, so a blank type is a rejection rather than a
 * reason to defer - allowing it here would only move the failure to Publish.
 */
export const isAllowedImageType = (file, allowedMediaTypes) => {
  const expected = allowedMediaTypes?.[getFileExtension(file?.name)];
  return Boolean(expected) && file?.type === expected;
};